/**
 * @fileoverview The one lesson filter bar (F4), rendered by every list of
 * lessons in both portals: the instructor's history, the org's class Lessons
 * tab, the org's program Sessions tab, the instructor's program Lessons tab.
 *
 * The model is `{ period, kind, instructor, classId, status, search }` and
 * lives with the caller (usually in the URL); this only draws the controls
 * the caller offers. A control whose options are missing is not drawn, so a
 * list with one instructor has no instructor control to disappoint with.
 * The org's copies filter metadata rows only — no summary is ever here to
 * search.
 *
 * Every control is a dropdown, the kind and the status included, so the whole
 * bar is one row of pickers above the list rather than rows of tabs. Each
 * "all" choice names what it covers ("All sessions", "All statuses"), since
 * a closed picker shows only its choice. `trailing` sits at the row's end —
 * the dashboard's Export.
 */

import { Icon } from "@/components/studio/primitives";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { KINDS, PERIODS, STATUSES } from "@/lib/lessonHistory";

const TRIGGER =
  "h-9 w-auto min-w-[128px] gap-2 rounded-full border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 font-sans text-[13px] text-[var(--text-primary)]";
const CONTENT = "border-[var(--studio-divider-strong)] bg-[var(--studio-overlay)]";
const ITEM = "font-sans text-[13px]";

function Picker({ testId, value, onChange, options, allLabel }) {
  return (
    <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? null : v)}>
      <SelectTrigger data-testid={testId} className={TRIGGER}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent className={CONTENT}>
        <SelectItem value="all" className={ITEM}>
          {allLabel}
        </SelectItem>
        {options.map((o) => (
          <SelectItem key={o.id} value={o.id} className={ITEM}>
            {o.name || o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** A choice list whose "all" entry is its own option (the kind, the status). */
function Choice({ testId, value, onChange, options, allLabel }) {
  return (
    <Select value={value || "all"} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className={TRIGGER}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent className={CONTENT}>
        {options.map((o) => (
          <SelectItem key={o.id} value={o.id} className={ITEM} data-testid={`${testId}-${o.id}`}>
            {o.id === "all" ? allLabel : o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * @param {{
 *   value: object, onChange: Function,
 *   periods?: Array, kinds?: Array|false, statuses?: Array|false,
 *   instructors?: Array<{id,name}>, classes?: Array<{id,name}>,
 *   programs?: Array<{id,name}>, unit?: string,
 *   searchPlaceholder?: string|false, children?: any, trailing?: any,
 *   className?: string,
 * }} props `unit` names the rows ("lessons", "sessions") for the kind
 *   picker's "all" choice.
 */
export default function LessonFilterBar({
  value,
  onChange,
  periods = PERIODS,
  kinds = false,
  statuses = STATUSES,
  instructors,
  classes,
  programs,
  unit = "lessons",
  searchPlaceholder = "Search lessons",
  children,
  trailing,
  className = "mb-4",
}) {
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`} data-testid="lesson-filter-bar">
      {periods && (
        <Select value={value.period} onValueChange={(period) => set({ period })}>
          <SelectTrigger data-testid="period-filter" className={TRIGGER}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className={CONTENT}>
            {periods.map((p) => (
              <SelectItem key={p.id} value={p.id} className={ITEM}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {kinds && (
        <Choice
          testId="kind-filter"
          options={kinds === true ? KINDS : kinds}
          value={value.kind}
          onChange={(kind) => set({ kind })}
          allLabel={`All ${unit}`}
        />
      )}

      {statuses && (
        <Choice
          testId="status-filter"
          options={statuses}
          value={value.status}
          onChange={(status) => set({ status })}
          allLabel="All statuses"
        />
      )}

      {/* Only the org-wide list offers programs: inside one program the
          picker would have a single entry. */}
      {programs && programs.length > 1 && (
        <Picker testId="program-filter" value={value.program} onChange={(program) => set({ program })} options={programs} allLabel="All programs" />
      )}

      {instructors && instructors.length > 1 && (
        <Picker testId="instructor-filter" value={value.instructor} onChange={(instructor) => set({ instructor })} options={instructors} allLabel="All instructors" />
      )}

      {classes && classes.length > 0 && (
        <Picker testId="class-filter" value={value.classId} onChange={(classId) => set({ classId })} options={classes} allLabel="All classes" />
      )}

      {children}

      {searchPlaceholder !== false && (
        <div className="relative min-w-[190px] flex-1 lg:max-w-[240px]">
          <Icon
            name="search"
            size={15}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-quaternary)]"
          />
          <input
            value={value.search || ""}
            onChange={(e) => set({ search: e.target.value })}
            placeholder={searchPlaceholder}
            data-testid="lesson-search"
            className="h-9 w-full rounded-full border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] pl-9 pr-3 font-sans text-[13px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-quaternary)] focus:border-[var(--accent-yellow)]"
          />
        </div>
      )}

      {trailing && <div className="ml-auto flex shrink-0 items-center gap-2">{trailing}</div>}
    </div>
  );
}
